import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { AuthImage } from "@/components/AuthImage";
import { displayDate } from "@/lib/dates";
import { MapPin, CalendarDays, HardHat } from "lucide-react";
import { STATUS_LABEL, tagIcon, initials, money, budgetUse } from "./jobsShared";
import { JobActions } from "./JobActions";

export function Thumb({ job }) {
  if (job.thumb_photo_path) return <AuthImage path={job.thumb_photo_path} alt={job.title} className="jr-thumb" />;
  return <div className="jr-thumb ph" aria-hidden="true"><HardHat className="w-6 h-6" /></div>;
}

export function TitleCell({ job }) {
  return (
    <div className="jr-title">
      <Thumb job={job} />
      <div className="min-w-0">
        <strong title={job.title}>{job.job_number && <span className="jr-number" data-testid={`jr-number-${job.id}`}>{job.job_number}</span>}
          <Link to={`/app/jobs/${encodeURIComponent(job.id)}`} data-testid={`jr-title-${job.id}`} onClick={(e) => e.stopPropagation()}>{job.title}</Link></strong>
        {job.client_name && <small>{job.client_name}</small>}
        {job.weather_tags?.length > 0 && <div className="jr-tags">{job.weather_tags.slice(0, 3).map((t) => { const I = tagIcon(t); return <span key={t} className="jr-tag"><I className="w-3 h-3" />{t}</span>; })}</div>}
      </div>
    </div>
  );
}

export function CrewCell({ ids = [], users }) {
  if (!ids.length) return <span className="jr-muted">—</span>;
  const name = (id) => users.find((u) => u.id === id)?.name;
  return <div className="jr-crew">{ids.slice(0, 3).map((id) => <b key={id} title={name(id) || ""}>{initials(name(id))}</b>)}{ids.length > 3 && <b className="more">+{ids.length - 3}</b>}</div>;
}

export function ProgressCell({ spend, finance }) {
  if (!finance) return <span className="jr-muted">Not tracked</span>;
  if (spend === undefined) return <span className="jr-muted">…</span>;
  const use = budgetUse(spend);
  if (!use) return <span className="jr-muted">No budget set</span>;
  return <div className="jr-prog" title="Cost so far as a share of the quoted value"><strong>{use.pct}%</strong><small>of budget used</small><div className="jr-bar"><i className={use.tone} style={{ width: `${Math.min(100, use.pct)}%` }} /></div></div>;
}

export function ValueCell({ spend, finance }) {
  if (!finance) return <span className="jr-muted">—</span>;
  if (spend === undefined) return <span className="jr-muted">…</span>;
  const over = spend?.quoted_value && (spend.total || 0) > spend.quoted_value;
  return <div className="jr-value">{spend?.quoted_value ? money(spend.quoted_value) : <span className="jr-muted">No quote</span>}<small className={over ? "over" : ""}>{money(spend?.total)} spent</small></div>;
}

export function DateCell({ job }) {
  if (!job.start_date && !job.end_date) return <span className="jr-muted">—</span>;
  return <div className="jr-date"><CalendarDays className="w-3.5 h-3.5 shrink-0 mt-0.5" /><span>{displayDate(job.start_date || job.end_date)}{job.start_date && job.end_date && job.end_date !== job.start_date && <><br />to {displayDate(job.end_date)}</>}</span></div>;
}

export const StatusPill = ({ job }) => <span className={`jr-status ${job.status}`} data-testid={`jr-status-${job.id}`}>{STATUS_LABEL[job.status] || job.status}</span>;

export const LocationCell = ({ job }) => <div className="jr-loc">{job.site_address && <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5" />}<span title={job.site_address || ""}>{job.site_address || "No site address"}</span></div>;

export function JobRow({ job, users, spend, finance, ctx }) {
  const over = finance && spend?.quoted_value && (spend.total || 0) > spend.quoted_value;
  const open = (e) => { if (!e.target.closest("a,button,[role=menuitem]")) ctx.navigate(`/app/jobs/${encodeURIComponent(job.id)}`); };
  return (
    <article className={`jr-job${over ? " over" : ""}`} tabIndex={0} onClick={open} onKeyDown={(e) => e.key === "Enter" && e.target === e.currentTarget && open(e)} data-testid={`jr-row-${job.id}`}>
      <TitleCell job={job} />
      <LocationCell job={job} />
      <div className="jr-c-crew"><CrewCell ids={job.assigned_user_ids} users={users} /></div>
      <div className="jr-c-prog"><ProgressCell spend={spend} finance={finance} /></div>
      <div className="jr-c-value"><ValueCell spend={spend} finance={finance} /></div>
      <div className="jr-c-date"><DateCell job={job} /></div>
      <div className="jr-st"><StatusPill job={job} /></div>
      <div className="jr-c-act"><JobActions job={job} ctx={ctx} /></div>
    </article>
  );
}

export function JobCardItem({ job, users, spend, finance, ctx }) {
  const open = (e) => { if (!e.target.closest("a,button,[role=menuitem]")) ctx.navigate(`/app/jobs/${encodeURIComponent(job.id)}`); };
  return (
    <article className="jr-job" tabIndex={0} onClick={open} data-testid={`jr-card-${job.id}`}>
      <div className="jr-cardrow"><TitleCell job={job} /><JobActions job={job} ctx={ctx} /></div>
      <LocationCell job={job} />
      <div className="jr-cardrow"><CrewCell ids={job.assigned_user_ids} users={users} /><DateCell job={job} /></div>
      <div className="jr-cardrow"><ProgressCell spend={spend} finance={finance} /><ValueCell spend={spend} finance={finance} /></div>
      <div><StatusPill job={job} /></div>
    </article>
  );
}

export function useJobsData(finance, manage) {
  const [state, setState] = useState({ jobs: [], loaded: false, error: false });
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [costs, setCosts] = useState(null);
  const load = () => {
    setState((s) => ({ ...s, error: false }));
    api.get("/jobs", { __flSkipReadCache: true })
      .then((r) => setState({ jobs: r.data || [], loaded: true, error: false }))
      .catch(() => setState((s) => ({ ...s, loaded: true, error: true })));
  };
  useEffect(() => {
    load();
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {});
    if (manage) api.get("/clients").then((r) => setClients(r.data || [])).catch(() => {});
    if (finance) api.get("/jobs/cost_summary").then((r) => setCosts(r.data.summaries || {})).catch(() => setCosts({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return { ...state, users, clients, costs, reload: load };
}
